import { useMemo } from "react";
import { Metric } from "@linkfluence/models/metric";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { DatavizHorizontalBarChart } from "@radarly/models/dataviz";
import { SearchFragmentClassification } from "@radarly/models/search";
import { useRangeFromFragment } from "../../data";
import { computeSeriesWithEvolutions } from "../compute";

interface HookProps {
  loading: boolean;
  data: SearchFragmentClassification["data"];
  comparaisonData: SearchFragmentClassification["data"];
  fragment: SearchFragmentClassification;
  dataviz: DatavizHorizontalBarChart;
}

function getPenetrationRateTotal(
  data: SearchFragmentClassification["data"],
  metric: Metric
) {
  return data && data.aggs.metrics ? data.aggs.metrics.metrics[metric] : null;
}

function getTotalFromData(
  data: SearchFragmentClassification["data"],
  metric: Metric
) {
  return data && data.aggs && data.aggs.metrics
    ? data.aggs.metrics.metrics[metric]
    : null;
}

export function useHorizontalBarChart({
  data,
  comparaisonData,
  dataviz,
  fragment,
  loading,
}: HookProps) {
  const locale = useCurrentUserLocale();
  const metric = fragment.request.aggs.contentClassification.metrics[0];
  const gauges = useMemo(() => {
    const series = data ? data.aggs.contentClassification.values : [];
    const comparaisonSeries = comparaisonData
      ? comparaisonData.aggs.contentClassification.values
      : [];
    const currentTotalCount = getTotalFromData(data, metric);
    const previousTotalCount = getTotalFromData(comparaisonData, metric);
    return computeSeriesWithEvolutions(
      series,
      comparaisonSeries,
      metric,
      "last",
      locale,
      true,
      dataviz.mainUnit,
      currentTotalCount,
      previousTotalCount
    );
  }, [data, comparaisonData, metric, locale, dataviz.mainUnit]);
  const total = getPenetrationRateTotal(data, metric);
  const range = useRangeFromFragment(fragment, loading);
  return {
    gauges,
    total,
    metric,
    range,
  };
}
